import { AdminConfiguration } from '@centecomm/admin-config-service';
import { PropertyOccupant, UserPermissions } from '@centecomm/portal-service';
import { useCurrentUser } from '@centecomm/portal-service-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import PropertyOccupantSelectionModal from 'Shared/PropertyOccupantSelection/PropertyOccupantSelectionModal';
import useSettingsFromState from 'State/Settings/Hooks/useSettingsFromState';
import useComplianceHubEnabled from 'utils/Hooks/LaunchDarkly/useComplianceHubEnabled';

import { NounIconUpload } from '../../../Icons';
import QuickActionButton from './QuickActionButton';

function HVACQuickActionModal(): JSX.Element | null {
    const { t } = useTranslation(['dashboardFeature']);
    const history = useHistory();
    const { isTenant } = useCurrentUser();
    const { settings } = useSettingsFromState<AdminConfiguration>();
    const hvacComplianceSettings = settings?.hvacCompliance;
    const complianceHubEnabled = useComplianceHubEnabled();

    const [show, setShow] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [property, setProperty] = useState<number | null>(null);
    const [occupant, setOccupant] = useState<number | null>(null);
    const [hasNoProperties, setHasNoProperties] = useState<boolean>(false);

    const routeToHVACCompliance = () => {
        history.push(
            isTenant
                ? `/business/${occupant}/hvac`
                : complianceHubEnabled
                ? `/compliance-hub/${property}/occupants/${occupant}/hvac`
                : `/locations/${property}/occupants/${occupant}/hvac`,
        );
    };

    const hvacEnabledOccupants = Object.keys(hvacComplianceSettings?.occupantsSettings || {}).filter(
        (occupantId) => hvacComplianceSettings?.occupantsSettings[occupantId].enabled,
    );

    const occupantFilter = (occupantToFilter: PropertyOccupant) => hvacEnabledOccupants.includes(`${occupantToFilter.id}`);

    useEffect(() => {
        if (show && occupant && property) routeToHVACCompliance();
    }, [property, occupant, show]);

    const buttonText = useMemo(() => {
        if (isTenant) {
            return t('dashboardFeature:links.hvacCompliance.buttons.submit');
        }
        return t('dashboardFeature:links.hvacCompliance.buttons.view');
    }, [isTenant, t]);
    const hasNoOccupants = useMemo(() => {
        if (isTenant) {
            return t('dashboardFeature:modals.hvacCompliance.errors.noBusinesses');
        }
        return t('dashboardFeature:modals.hvacCompliance.errors.noOccupants');
    }, [isTenant, t]);

    if (!hvacEnabledOccupants.length || hasNoProperties) return null;

    return (
        <>
            {!loading && <QuickActionButton Icon={NounIconUpload} setShow={setShow} buttonText={buttonText} />}
            <PropertyOccupantSelectionModal
                modalHeader={t('dashboardFeature:modals.hvacCompliance.header')}
                noOccupantsPlaceholder={hasNoOccupants}
                occupantsPlaceholder={isTenant ? t('dashboardFeature:modals.hvacCompliance.placeholder') : ''}
                show={show}
                setShow={setShow}
                userPermission={UserPermissions.ViewHvacCompliance}
                propertyId={property}
                occupantId={occupant}
                setPropertyId={setProperty}
                setOccupantId={setOccupant}
                setLoading={setLoading}
                occupantFilter={occupantFilter}
                setHasNoProperties={setHasNoProperties}
            />
        </>
    );
}

export default HVACQuickActionModal;
